import { useState } from 'react';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { ACCESS_REVIEWERS } from '../lib/access';
import type { AccessReviewer } from '../lib/access';
import { formatDateTime } from '../lib/format';
import { RefreshButton } from './RefreshButton';
import '../styles/access-assignees.css';

/**
 * Default access assignees (Settings → Default access assignees, root+maintainer).
 * One flat list: every new access task starts with these people assigned (the requester
 * may remove them or add anyone). Rows are the layer-1 rows of `access_reviewers`; the API
 * lists and writes only those, so this page never sends or shows a layer. Removing a
 * person never touches open tasks (assignees are snapshotted per task).
 */
const CREATE = gql`
  mutation CreateAccessReviewer($email: String!, $displayName: String) {
    createAccessReviewer(email: $email, displayName: $displayName) {
      id
    }
  }
`;
const REMOVE = gql`
  mutation DeleteAccessReviewer($id: Int!) {
    deleteAccessReviewer(id: $id)
  }
`;

/** One person, however many legacy layer rows they had (removing them removes every row). */
interface Person {
  email: string;
  displayName?: string;
  createdBy: string;
  createdAt: string;
  ids: string[];
}

function people(list: AccessReviewer[]): Person[] {
  const by = new Map<string, Person>();
  for (const r of list) {
    const k = r.email.toLowerCase();
    const p = by.get(k);
    if (p) {
      p.ids.push(r.id);
      p.displayName ||= r.displayName;
    } else by.set(k, { email: r.email, displayName: r.displayName, createdBy: r.createdBy, createdAt: r.createdAt, ids: [r.id] });
  }
  return [...by.values()].sort((a, b) => a.email.localeCompare(b.email));
}

export function AccessReviewers() {
  const { data, loading, error, refetch } = useQuery<{ accessReviewers: AccessReviewer[] }>(ACCESS_REVIEWERS, {
    fetchPolicy: 'cache-and-network',
  });
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [mutErr, setMutErr] = useState<string | null>(null);

  const onDone = () => {
    setMutErr(null);
    void refetch();
  };
  const onErr = (e: Error) => setMutErr(e.message);
  const [create, { loading: creating }] = useMutation(CREATE, { onCompleted: onDone, onError: onErr });
  const [remove, { loading: removing }] = useMutation(REMOVE, { onCompleted: onDone, onError: onErr });

  const rows = people(data?.accessReviewers ?? []);
  const busy = creating || removing;
  const canAdd = !busy && email.trim().includes('@');

  if (error) return <p className="error">{error.message}</p>;

  return (
    <div className="stack" data-testid="access-assignees">
      <form
        className="filter-row access-assignees__add"
        onSubmit={(e) => {
          e.preventDefault();
          if (!canAdd) return;
          void create({ variables: { email: email.trim(), displayName: name.trim() || null } });
          setEmail('');
          setName('');
        }}
      >
        <input
          type="email"
          aria-label="Email"
          placeholder="person@optisigns.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <input
          type="text"
          aria-label="Display name"
          placeholder="Display name (optional)"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <button type="submit" className="btn btn-primary" disabled={!canAdd}>
          Add person
        </button>
      </form>
      {mutErr && <p className="error">{mutErr}</p>}
      <div className="access-assignees__count">
        <span className="muted text-xs">
          <strong>{rows.length}</strong> {rows.length === 1 ? 'person' : 'people'}
        </span>
        <RefreshButton refetch={() => refetch().catch(() => undefined)} />
      </div>

      {loading && !rows.length ? (
        <p className="muted">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          <div className="empty-title">No default access assignees</div>
          <div className="empty-body">
            New access tasks stay unassigned unless the requester picks someone. Add at least one person.
          </div>
        </div>
      ) : (
        <div className="access-assignees__table-wrap">
        <table className="data-list--primary" aria-label="Default access assignees">
          <thead>
            <tr>
              <th scope="col">Email</th>
              <th scope="col">Name</th>
              <th scope="col">Added by</th>
              <th scope="col">
                <span className="access-assignees__sr">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.email}>
                <td>{r.email}</td>
                <td>{r.displayName}</td>
                <td title={formatDateTime(r.createdAt)}>
                  {r.createdBy}
                </td>
                <td className="access-assignees__actions">
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    disabled={busy}
                    onClick={() => {
                      if (window.confirm(`Remove ${r.email} from the default access assignees? Open tasks keep them assigned.`))
                        void Promise.all(r.ids.map((id) => remove({ variables: { id: Number(id) } })));
                    }}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}
